import React, { useState, useRef, useEffect, useCallback } from "react";
import { api } from "../api";

const TEMPLATES = [
  { id: "sleek-dark", name: "Sleek Dark", bg: "#0a0a0a", accent: "#ffffff", gradient: ["#0a0a0a", "#1a1a2e"], overlay: "dark" },
  { id: "fire-sale", name: "Fire Sale", bg: "#1a0000", accent: "#ff4444", gradient: ["#1a0000", "#3d0000", "#ff4444"], overlay: "fire" },
  { id: "luxury-gold", name: "Luxury Gold", bg: "#0d0d0d", accent: "#d4af37", gradient: ["#0d0d0d", "#1a1500", "#2a2000"], overlay: "luxury" },
  { id: "ocean-blue", name: "Ocean Blue", bg: "#001529", accent: "#00b4d8", gradient: ["#001529", "#002d4a", "#003d66"], overlay: "wave" },
  { id: "neon-pink", name: "Neon Pink", bg: "#0a0014", accent: "#ff006e", gradient: ["#0a0014", "#1a0030", "#2d004d"], overlay: "neon" },
  { id: "fresh-green", name: "Fresh Green", bg: "#001a0a", accent: "#00ff88", gradient: ["#001a0a", "#003316", "#004d22"], overlay: "nature" },
  { id: "sunset-warm", name: "Sunset", bg: "#1a0a00", accent: "#ff8c00", gradient: ["#1a0a00", "#331a00", "#4d2600"], overlay: "warm" },
  { id: "clean-white", name: "Clean White", bg: "#ffffff", accent: "#111111", gradient: ["#ffffff", "#f5f5f5", "#eeeeee"], overlay: "light" },
  { id: "royal-purple", name: "Royal Purple", bg: "#0f0020", accent: "#9b59b6", gradient: ["#0f0020", "#1a0033", "#2d0057"], overlay: "royal" },
];

const ELEMENT_SHAPES = [
  { id: "none", name: "None", icon: "—" },
  { id: "circles", name: "Circles", icon: "◯" },
  { id: "dots", name: "Dots", icon: "•••" },
  { id: "lines", name: "Lines", icon: "╱╲" },
  { id: "squares", name: "Squares", icon: "◻" },
  { id: "stars", name: "Stars", icon: "✦" },
];

const BADGE_STYLES = [
  { id: "none", name: "No Badge" },
  { id: "ribbon", name: "Ribbon" },
  { id: "circle", name: "Circle Badge" },
  { id: "banner", name: "Banner" },
  { id: "sticker", name: "Sticker" },
];

const DIMENSIONS = [
  { id: "insta-post", name: "Instagram Post", w: 1080, h: 1080, icon: "◻" },
  { id: "insta-story", name: "Instagram Story", w: 1080, h: 1920, icon: "▯" },
  { id: "fb-post", name: "Facebook Post", w: 1200, h: 630, icon: "▭" },
  { id: "twitter", name: "Twitter/X Post", w: 1600, h: 900, icon: "▬" },
  { id: "linkedin", name: "LinkedIn Post", w: 1200, h: 627, icon: "▭" },
  { id: "pinterest", name: "Pinterest Pin", w: 1000, h: 1500, icon: "▯" },
  { id: "youtube-thumb", name: "YouTube Thumb", w: 1280, h: 720, icon: "▬" },
  { id: "fullscreen", name: "Full Screen 16:9", w: 1920, h: 1080, icon: "🖥" },
];

export default function PromoTemplateGenerator({ productImage, productImageB64, productName, onGenerated, onClose }) {
  const canvasRef = useRef(null);
  const [template, setTemplate] = useState(TEMPLATES[0]);
  const [dimension, setDimension] = useState(DIMENSIONS[0]);
  const [headline, setHeadline] = useState("NEW ARRIVAL");
  const [subtext, setSubtext] = useState("SHOP NOW →");
  const [brandName, setBrandName] = useState("");
  const [discount, setDiscount] = useState("");
  const [elements, setElements] = useState("circles");
  const [badgeStyle, setBadgeStyle] = useState("none");
  const [removeBg, setRemoveBg] = useState(false);
  const [removingBg, setRemovingBg] = useState(false);
  const [filter, setFilter] = useState("none");
  const [productScale, setProductScale] = useState(70);
  const [productX, setProductX] = useState(50); // percentage 0-100 (center = 50)
  const [productY, setProductY] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [textPosition, setTextPosition] = useState("top");
  const [previewUrl, setPreviewUrl] = useState("");
  const [loadedImg, setLoadedImg] = useState(null);
  const [originalImg, setOriginalImg] = useState(null);

  const FILTERS = [
    { id: "none", name: "Original" },
    { id: "bw", name: "B&W" },
    { id: "vintage", name: "Vintage" },
    { id: "dramatic", name: "Dramatic" },
    { id: "warm", name: "Warm Glow" },
    { id: "cool", name: "Cool Tone" },
    { id: "high-contrast", name: "High Contrast" },
    { id: "sepia", name: "Sepia" },
    { id: "cartoon", name: "Cartoon" },
  ];

  const handleRemoveBg = async () => {
    if (!productImage && !productImageB64) return;
    setRemovingBg(true);
    try {
      // Send base64 data to backend
      const imageData = productImageB64
        ? `data:image/png;base64,${productImageB64}`
        : productImage;
      const resp = await api.post("/social/remove-background", {
        image_url: imageData,
      });
      if (resp.data.image_url) {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          setLoadedImg(img);
          setRemoveBg(true);
        };
        img.onerror = () => { setRemovingBg(false); };
        img.src = resp.data.image_url;
      } else {
        console.error("BG removal: no image returned");
      }
    } catch (e) {
      console.error("BG removal failed:", e?.response?.data?.error || e.message);
    } finally {
      setRemovingBg(false);
    }
  };

  const handleRestoreOriginal = () => {
    if (originalImg) {
      setLoadedImg(originalImg);
      setRemoveBg(false);
    }
  };

  const applyFilter = (ctx, W, H) => {
    if (filter === "none") return;
    const imageData = ctx.getImageData(0, 0, W, H);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i], g = data[i + 1], b = data[i + 2];

      if (filter === "bw") {
        const gray = r * 0.299 + g * 0.587 + b * 0.114;
        data[i] = data[i + 1] = data[i + 2] = gray;
      } else if (filter === "vintage") {
        data[i] = Math.min(255, r * 1.1 + 20);
        data[i + 1] = Math.min(255, g * 0.9 + 10);
        data[i + 2] = Math.max(0, b * 0.8);
      } else if (filter === "dramatic") {
        data[i] = Math.min(255, r * 1.3 - 30);
        data[i + 1] = Math.min(255, g * 1.1 - 20);
        data[i + 2] = Math.min(255, b * 1.1 - 10);
      } else if (filter === "warm") {
        data[i] = Math.min(255, r * 1.15 + 10);
        data[i + 1] = Math.min(255, g * 1.05);
        data[i + 2] = Math.max(0, b * 0.85);
      } else if (filter === "cool") {
        data[i] = Math.max(0, r * 0.85);
        data[i + 1] = Math.min(255, g * 1.0);
        data[i + 2] = Math.min(255, b * 1.2 + 15);
      } else if (filter === "high-contrast") {
        data[i] = r > 128 ? Math.min(255, r * 1.4) : Math.max(0, r * 0.6);
        data[i + 1] = g > 128 ? Math.min(255, g * 1.4) : Math.max(0, g * 0.6);
        data[i + 2] = b > 128 ? Math.min(255, b * 1.4) : Math.max(0, b * 0.6);
      } else if (filter === "sepia") {
        data[i] = Math.min(255, r * 0.393 + g * 0.769 + b * 0.189);
        data[i + 1] = Math.min(255, r * 0.349 + g * 0.686 + b * 0.168);
        data[i + 2] = Math.min(255, r * 0.272 + g * 0.534 + b * 0.131);
      } else if (filter === "cartoon") {
        data[i] = Math.round(r / 40) * 40;
        data[i + 1] = Math.round(g / 40) * 40;
        data[i + 2] = Math.round(b / 40) * 40;
      }
    }
    ctx.putImageData(imageData, 0, 0);
  };

  // Load product image — only once on mount
  useEffect(() => {
    if (!productImage || originalImg) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => { setLoadedImg(img); setOriginalImg(img); };
    img.src = productImage;
  }, [productImage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Render canvas whenever settings change
  useEffect(() => {
    renderCanvas();
  }, [template, dimension, headline, subtext, brandName, discount, elements, badgeStyle, removeBg, productScale, productX, productY, textPosition, loadedImg, filter]);

  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = dimension.w, H = dimension.h;
    canvas.width = W;
    canvas.height = H;

    // === BACKGROUND ===
    const grad = ctx.createLinearGradient(0, 0, W, H);
    template.gradient.forEach((c, i) => grad.addColorStop(i / (template.gradient.length - 1), c));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    // === DECORATIVE ELEMENTS ===
    drawElements(ctx, W, H);

    // === PRODUCT IMAGE ===
    if (loadedImg) {
      const scale = productScale / 100;
      const maxDim = Math.min(W, H) * 0.65 * scale;
      const imgScale = Math.min(maxDim / loadedImg.width, maxDim / loadedImg.height);
      const imgW = loadedImg.width * imgScale;
      const imgH = loadedImg.height * imgScale;
      // Position based on productX/Y (0-100 percentage)
      const imgX = (W - imgW) * (productX / 100);
      const imgY = (H - imgH) * (productY / 100);

      // Product shadow
      ctx.save();
      ctx.shadowColor = template.accent + "40";
      ctx.shadowBlur = 60;
      ctx.shadowOffsetY = 30;
      ctx.drawImage(loadedImg, imgX, imgY, imgW, imgH);
      ctx.restore();

      // Glow ring behind product
      ctx.save();
      ctx.globalAlpha = 0.1;
      ctx.beginPath();
      ctx.ellipse(imgX + imgW / 2, imgY + imgH - 10, imgW * 0.4, 20, 0, 0, Math.PI * 2);
      ctx.fillStyle = template.accent;
      ctx.fill();
      ctx.restore();
    }

    // === TEXT ===
    drawText(ctx, W, H);

    // === BADGE ===
    if (discount && badgeStyle !== "none") {
      drawBadge(ctx, W, H);
    }

    // === BRAND WATERMARK ===
    if (brandName) {
      ctx.save();
      ctx.globalAlpha = 0.5;
      ctx.font = "600 16px system-ui, sans-serif";
      ctx.fillStyle = template.overlay === "light" ? "#666" : "#888";
      ctx.textAlign = "center";
      ctx.fillText(brandName, W / 2, H - 25);
      ctx.restore();
    }

    // === BORDER FRAME ===
    ctx.strokeStyle = template.accent + "30";
    ctx.lineWidth = 2;
    ctx.strokeRect(24, 24, W - 48, H - 48);

    // === APPLY FILTER ===
    if (filter !== "none") {
      applyFilter(ctx, W, H);
    }

    setPreviewUrl(canvas.toDataURL("image/png"));
  }, [template, dimension, headline, subtext, brandName, discount, elements, badgeStyle, productScale, productX, productY, textPosition, loadedImg, filter]);

  const drawElements = (ctx, W, H) => {
    ctx.save();
    ctx.globalAlpha = 0.12;

    if (elements === "circles") {
      for (let i = 0; i < 8; i++) {
        ctx.beginPath();
        ctx.arc(Math.random() * W, Math.random() * H, 30 + Math.random() * 80, 0, Math.PI * 2);
        ctx.strokeStyle = template.accent;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    } else if (elements === "dots") {
      for (let i = 0; i < 60; i++) {
        ctx.beginPath();
        ctx.arc(Math.random() * W, Math.random() * H, 2 + Math.random() * 4, 0, Math.PI * 2);
        ctx.fillStyle = template.accent;
        ctx.fill();
      }
    } else if (elements === "lines") {
      for (let i = 0; i < 12; i++) {
        ctx.beginPath();
        const x = Math.random() * W;
        const y = Math.random() * H;
        ctx.moveTo(x, y);
        ctx.lineTo(x + 100 + Math.random() * 200, y + 100 + Math.random() * 200);
        ctx.strokeStyle = template.accent;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    } else if (elements === "squares") {
      for (let i = 0; i < 6; i++) {
        const size = 40 + Math.random() * 80;
        ctx.strokeStyle = template.accent;
        ctx.lineWidth = 1.5;
        ctx.strokeRect(Math.random() * W, Math.random() * H, size, size);
      }
    } else if (elements === "stars") {
      ctx.font = `${20 + Math.random() * 30}px serif`;
      ctx.fillStyle = template.accent;
      for (let i = 0; i < 15; i++) {
        ctx.fillText("✦", Math.random() * W, Math.random() * H);
      }
    }

    // Corner accents
    ctx.globalAlpha = 0.2;
    ctx.strokeStyle = template.accent;
    ctx.lineWidth = 2;
    // Top-left
    ctx.beginPath(); ctx.moveTo(40, 80); ctx.lineTo(40, 40); ctx.lineTo(80, 40); ctx.stroke();
    // Top-right
    ctx.beginPath(); ctx.moveTo(W - 80, 40); ctx.lineTo(W - 40, 40); ctx.lineTo(W - 40, 80); ctx.stroke();
    // Bottom-left
    ctx.beginPath(); ctx.moveTo(40, H - 80); ctx.lineTo(40, H - 40); ctx.lineTo(80, H - 40); ctx.stroke();
    // Bottom-right
    ctx.beginPath(); ctx.moveTo(W - 80, H - 40); ctx.lineTo(W - 40, H - 40); ctx.lineTo(W - 40, H - 80); ctx.stroke();

    ctx.restore();
  };

  const drawText = (ctx, W, H) => {
    const isLight = template.overlay === "light";
    const textColor = isLight ? "#111111" : "#ffffff";

    if (textPosition === "top") {
      // Headline at top
      ctx.textAlign = "center";
      ctx.fillStyle = textColor;
      ctx.font = `900 ${headline.length > 20 ? 48 : headline.length > 12 ? 58 : 72}px system-ui, sans-serif`;
      ctx.fillText(headline.toUpperCase(), W / 2, 120);

      // Accent underline
      const textW = ctx.measureText(headline.toUpperCase()).width;
      ctx.fillStyle = template.accent;
      ctx.fillRect(W / 2 - textW / 2, 135, textW, 4);

      // Subtext at bottom
      ctx.font = `600 24px system-ui, sans-serif`;
      ctx.fillStyle = template.accent;
      ctx.textAlign = "center";
      ctx.fillText(subtext, W / 2, H - 70);
    } else {
      // Text at bottom
      // Dark overlay at bottom for text
      const bottomGrad = ctx.createLinearGradient(0, H * 0.7, 0, H);
      bottomGrad.addColorStop(0, "rgba(0,0,0,0)");
      bottomGrad.addColorStop(1, "rgba(0,0,0,0.8)");
      ctx.fillStyle = bottomGrad;
      ctx.fillRect(0, H * 0.7, W, H * 0.3);

      ctx.textAlign = "center";
      ctx.fillStyle = "#ffffff";
      ctx.font = `900 ${headline.length > 20 ? 42 : 56}px system-ui, sans-serif`;
      ctx.fillText(headline.toUpperCase(), W / 2, H - 120);

      ctx.font = `600 22px system-ui, sans-serif`;
      ctx.fillStyle = template.accent;
      ctx.fillText(subtext, W / 2, H - 70);
    }
  };

  const drawBadge = (ctx, W, H) => {
    ctx.save();
    const badgeText = discount;

    if (badgeStyle === "ribbon") {
      // Ribbon in top-right corner
      ctx.translate(W - 120, 0);
      ctx.rotate(0);
      ctx.fillStyle = template.accent;
      ctx.fillRect(0, 50, 160, 50);
      // Triangle folds
      ctx.beginPath(); ctx.moveTo(0, 100); ctx.lineTo(15, 85); ctx.lineTo(0, 85); ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 22px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(badgeText, 80, 83);
    } else if (badgeStyle === "circle") {
      // Circle badge top-right
      ctx.beginPath();
      ctx.arc(W - 120, 120, 55, 0, Math.PI * 2);
      ctx.fillStyle = template.accent;
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 20px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(badgeText, W - 120, 127);
    } else if (badgeStyle === "banner") {
      // Banner across top
      ctx.fillStyle = template.accent;
      ctx.fillRect(0, 160, W, 55);
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 26px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(`★  ${badgeText}  ★`, W / 2, 195);
    } else if (badgeStyle === "sticker") {
      // Rotated sticker
      ctx.translate(W - 140, 140);
      ctx.rotate(-0.2);
      ctx.fillStyle = template.accent;
      ctx.beginPath();
      ctx.roundRect(-50, -30, 100, 60, 8);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 20px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(badgeText, 0, 7);
    }

    ctx.restore();
  };

  const handleDownload = () => {
    if (!previewUrl) return;
    const link = document.createElement("a");
    link.download = `promo-${headline.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}.png`;
    link.href = previewUrl;
    link.click();
  };

  const handleUse = () => {
    if (onGenerated && previewUrl) onGenerated(previewUrl);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0d1117] border border-white/10 rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/8 bg-[#0d1117]/95 backdrop-blur-sm">
          <div>
            <h2 className="text-white font-bold text-lg">Promotional Template Studio</h2>
            <p className="text-gray-500 text-xs">Customize your product promotional post</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-all">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Preview - 3 cols */}
          <div className="lg:col-span-3 space-y-3">
            <canvas ref={canvasRef}
              className={`w-full rounded-xl border border-white/10 bg-black ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
              style={{ aspectRatio: `${dimension.w}/${dimension.h}`, maxHeight: "500px", objectFit: "contain" }}
              onMouseDown={(e) => { setIsDragging(true); }}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
              onMouseMove={(e) => {
                if (!isDragging) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width) * 100;
                const y = ((e.clientY - rect.top) / rect.height) * 100;
                setProductX(Math.max(0, Math.min(100, x)));
                setProductY(Math.max(0, Math.min(100, y)));
              }}
              onTouchStart={() => setIsDragging(true)}
              onTouchEnd={() => setIsDragging(false)}
              onTouchMove={(e) => {
                const touch = e.touches[0];
                const rect = e.currentTarget.getBoundingClientRect();
                const x = ((touch.clientX - rect.left) / rect.width) * 100;
                const y = ((touch.clientY - rect.top) / rect.height) * 100;
                setProductX(Math.max(0, Math.min(100, x)));
                setProductY(Math.max(0, Math.min(100, y)));
              }}
              onWheel={(e) => {
                e.preventDefault();
                setProductScale(prev => Math.max(20, Math.min(120, prev + (e.deltaY > 0 ? -3 : 3))));
              }}
            />
            <p className="text-[10px] text-gray-600 text-center mt-1">Drag product to reposition · Scroll to resize</p>
            <div className="flex gap-2">
              <button onClick={handleDownload} className="flex-1 py-2.5 rounded-lg bg-white/5 border border-white/10 text-xs text-gray-300 hover:bg-white/10 transition-all font-medium">
                ⬇ Download PNG
              </button>
              <button onClick={handleUse} className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold hover:opacity-90 transition-all">
                ✓ Use as Post Image
              </button>
            </div>
          </div>

          {/* Controls - 2 cols */}
          <div className="lg:col-span-2 space-y-4 overflow-y-auto max-h-[70vh]">
            {/* Templates */}
            <div>
              <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest block mb-2">Template Style</label>
              <div className="grid grid-cols-3 gap-1.5">
                {TEMPLATES.map((t) => (
                  <button key={t.id} onClick={() => setTemplate(t)}
                    className={`p-2 rounded-lg text-[9px] font-semibold transition-all ${template.id === t.id ? "ring-1 ring-white/40 bg-white/10 text-white" : "bg-white/[0.03] text-gray-500 hover:bg-white/[0.06]"}`}>
                    <div className="w-full h-5 rounded mb-1" style={{ background: `linear-gradient(135deg, ${t.gradient[0]}, ${t.gradient[t.gradient.length - 1]})`, border: `1px solid ${t.accent}30` }} />
                    {t.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Dimensions */}
            <div>
              <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest block mb-2">Dimensions</label>
              <div className="grid grid-cols-2 gap-1.5">
                {DIMENSIONS.map((d) => (
                  <button key={d.id} onClick={() => setDimension(d)}
                    className={`px-2 py-1.5 rounded-lg text-[9px] font-semibold transition-all flex items-center gap-1.5 ${
                      dimension.id === d.id ? "ring-1 ring-white/40 bg-white/10 text-white" : "bg-white/[0.03] text-gray-500 hover:bg-white/[0.06]"
                    }`}>
                    <span>{d.icon}</span>
                    <span>{d.name}</span>
                    <span className="ml-auto text-[8px] text-gray-600">{d.w}x{d.h}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Headline */}
            <div>
              <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest block mb-1">Headline</label>
              <input value={headline} onChange={(e) => setHeadline(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:border-white/25" placeholder="NEW ARRIVAL" />
            </div>

            {/* Subtext */}
            <div>
              <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest block mb-1">Subtext / CTA</label>
              <input value={subtext} onChange={(e) => setSubtext(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:border-white/25" placeholder="SHOP NOW →" />
            </div>

            {/* Brand */}
            <div>
              <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest block mb-1">Brand Name (watermark)</label>
              <input value={brandName} onChange={(e) => setBrandName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:border-white/25" placeholder="Your Brand" />
            </div>

            {/* Discount + Badge */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest block mb-1">Discount</label>
                <input value={discount} onChange={(e) => setDiscount(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:border-white/25" placeholder="50% OFF" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest block mb-1">Badge Style</label>
                <select value={badgeStyle} onChange={(e) => setBadgeStyle(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm focus:outline-none" style={{ colorScheme: "dark" }}>
                  {BADGE_STYLES.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            </div>

            {/* Elements */}
            <div>
              <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest block mb-2">Decorative Elements</label>
              <div className="flex gap-1.5 flex-wrap">
                {ELEMENT_SHAPES.map(e => (
                  <button key={e.id} onClick={() => setElements(e.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs transition-all ${elements === e.id ? "bg-white/10 border border-white/30 text-white" : "bg-white/[0.03] border border-white/8 text-gray-500"}`}>
                    {e.icon} {e.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Background Removal */}
            <div>
              <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest block mb-2">Background</label>
              <div className="flex gap-2">
                <button onClick={handleRemoveBg} disabled={removingBg || removeBg}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
                    removeBg ? "bg-green-500/15 border border-green-500/30 text-green-400" : "bg-white/[0.03] border border-white/8 text-gray-400 hover:border-white/20"
                  }`}>
                  {removingBg ? (
                    <><svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="50 20"/></svg> Removing...</>
                  ) : removeBg ? "✓ BG Removed" : "Remove Background"}
                </button>
                {removeBg && (
                  <button onClick={handleRestoreOriginal}
                    className="px-3 py-2 rounded-lg text-xs bg-white/[0.03] border border-white/8 text-gray-500 hover:text-white transition-all">
                    Restore
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div>
              <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest block mb-2">Filters</label>
              <div className="grid grid-cols-3 gap-1.5">
                {FILTERS.map(f => (
                  <button key={f.id} onClick={() => setFilter(f.id)}
                    className={`px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${
                      filter === f.id ? "bg-white/10 border border-white/30 text-white" : "bg-white/[0.03] border border-white/8 text-gray-500 hover:text-gray-300"
                    }`}>
                    {f.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Product Scale & Position */}
            <div className="space-y-2">
              <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest block">Product Size: {productScale}%</label>
              <input type="range" min="20" max="120" value={productScale} onChange={(e) => setProductScale(Number(e.target.value))}
                className="w-full h-1.5 rounded-full appearance-none bg-white/10 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] text-gray-600 block mb-0.5">X Position: {Math.round(productX)}%</label>
                  <input type="range" min="0" max="100" value={productX} onChange={(e) => setProductX(Number(e.target.value))}
                    className="w-full h-1 rounded-full appearance-none bg-white/10 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white/70" />
                </div>
                <div>
                  <label className="text-[9px] text-gray-600 block mb-0.5">Y Position: {Math.round(productY)}%</label>
                  <input type="range" min="0" max="100" value={productY} onChange={(e) => setProductY(Number(e.target.value))}
                    className="w-full h-1 rounded-full appearance-none bg-white/10 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white/70" />
                </div>
              </div>
              <button onClick={() => { setProductX(50); setProductY(50); setProductScale(70); }}
                className="w-full py-1.5 rounded-lg text-[10px] bg-white/[0.03] border border-white/8 text-gray-500 hover:text-white transition-all">
                Reset Position
              </button>
            </div>

            {/* Text Position */}
            <div>
              <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest block mb-2">Text Position</label>
              <div className="flex gap-2">
                <button onClick={() => setTextPosition("top")}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${textPosition === "top" ? "bg-white/10 border border-white/30 text-white" : "bg-white/[0.03] border border-white/8 text-gray-500"}`}>
                  ↑ Top
                </button>
                <button onClick={() => setTextPosition("bottom")}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${textPosition === "bottom" ? "bg-white/10 border border-white/30 text-white" : "bg-white/[0.03] border border-white/8 text-gray-500"}`}>
                  ↓ Bottom
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
